import { httpsCallable } from 'firebase/functions';
import { Upload } from 'lucide-react';
import { useState } from 'react';
import { useExpiredSessionErrorToNavigate } from 'src/hooks/useExpiredSessionErrorToNavigate';
import { CLOUD_FUNCTIONS } from '../../../../../auth/FirebaseContext';
import Label from '../../../../../components/label/Label';
import { Alert, AlertDescription } from '../../../../../components/ui/Alert';
import { Button } from '../../../../../components/ui/Button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../../../components/ui/Card';
import {
  COIModel,
  COIStatus,
  coiRecordType
} from '../../../../../models/owner-portal/listingGeneral';
import { usePatchRTKQListingCOIMutation } from '../../../../../redux/rtkQuery/apiSlice';
import { fCurrency } from '../../../../../utils/formatNumber';
import { FDateFormat, fDate } from '../../../../../utils/formatTime';
import { cloud_postCOIPdf } from '../../../../../utils/owner-portal/cloudFunctions';
import FileNewFolderDialogWithFilter from '../../../file/portal/FileNewFolderDialogWithFilter';

interface ListingCOICardI {
  model: COIModel;
  modelLoading: boolean;
}

export default function ListingCOICard(props: ListingCOICardI) {
  const { model, modelLoading } = props;
  const handleExpiredSession = useExpiredSessionErrorToNavigate();

  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false);
  const [submissionProcessing, setSubmissionProcessing] = useState(false);

  const [
    sendLMIUpdatePatch // This is the mutation trigger function.
  ] = usePatchRTKQListingCOIMutation();

  const handleOpenUploadFile = () => {
    setUploadFileDialogOpen(true);
  };

  const handleCloseUploadFile = () => {
    setUploadFileDialogOpen(false);
  };

  if (model.type !== coiRecordType) {
    console.warn('invalid model sent to COI card: ' + model.type);
    return null;
  }

  const submissionRequired = model.CheckSubmissionRequired();

  const handleUpload: (a: (File | string)[]) => Promise<void> = async (a) => {
    const fileInfo = a[0];

    //NOTE: We limit to one file in this context, so this is just added complexity.
    //		Of course we'll bring it back if the file limit increases.
    // await Promise.all(a.map(async (fileInfo) => {
    const data: { object_id: string; value: any } = {
      value: '',
      object_id: model.id
    };

    // start showing an upload spinner
    setSubmissionProcessing(true);

    if (typeof fileInfo !== 'string') {
      const reader = new FileReader();
      const readerData = await new Promise((resolve) => {
        reader.onloadend = () => {
          resolve(reader.result);
        };
        //! important that we send up a data url
        // cloudfunction will use this to create a blob for salesforce
        reader.readAsDataURL(fileInfo);
      });
      data.value = readerData;
    } else {
      data.value = fileInfo;
    }

    const cloudFunction = httpsCallable(CLOUD_FUNCTIONS, cloud_postCOIPdf);
    const cloudResult = await cloudFunction(data)
      .then(async (result) => {
        if (result) {
          // console.warn('upload result', result);

          await sendLMIUpdatePatch({
            item_id: model.id,
            status: 'Pending Verification'
          });

          // console.log('after', patchResponse,
          // 			'| objects', lmiUpdateData, lmiUpdateError,
          // 			'| states', lmiUpdateIsUninitialized, lmiUpdateIsLoading, lmiUpdateIsSuccess, lmiUpdateIsError);
        }

        return { data: true };
      })
      .catch((error) => {
        console.log('file post error', error);
        handleExpiredSession(error);
        // this format is required by RTKQ
        return { error: error };
      })
      .finally(() => {
        // hide the upload spinner
        setSubmissionProcessing(false);
      });

    console.log('upload done', cloudResult);
    // return cloudResult
    // }))
  };

  const supportedExtensionsDesc = 'Accepts PDF and image files.';
  const supportedExtensionsRegEx = /\.(pdf|png|jpg|jpeg)$/i;
  const fileCountLimit = 1;

  const getLabelColor = () => {
    if (model.status === COIStatus.Expired) return 'error';
    if (model.status === COIStatus.Current) return 'secondary';
    return 'primary';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 p-4 sm:p-6">
        <div className="space-y-1.5">
          <CardTitle className="text-xl">{coiRecordType}</CardTitle>
          {submissionRequired && <CardDescription>Action Required</CardDescription>}
        </div>
        <Label color={getLabelColor()}>{model.status}</Label>
      </CardHeader>
      <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
        {submissionRequired ? (
          <>
            <Alert variant="warning" className="mb-4">
              <AlertDescription>
                An updated Certificate of Insurance is required for this property. Please upload an
                image or PDF.
              </AlertDescription>
            </Alert>

            <Button onClick={handleOpenUploadFile}>
              <Upload className="mr-2 h-4 w-4" />
              Upload
            </Button>

            <FileNewFolderDialogWithFilter
              open={uploadFileDialogOpen}
              busy={submissionProcessing || modelLoading}
              supportedExtensionsDesc={supportedExtensionsDesc}
              supportedExtensionsRegEx={supportedExtensionsRegEx}
              fileCountLimit={fileCountLimit}
              onClose={handleCloseUploadFile}
              onUpload={handleUpload}
            />
          </>
        ) : model.status === COIStatus.PendingVerification ? (
          <p className="text-sm text-muted-foreground">
            We are in the process of verifying your Certificate of Insurance.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-1 sm:col-span-2">
              <p className="text-xs font-medium text-muted-foreground">Provider</p>
              <p className="text-sm font-medium text-foreground">{model.vendor_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Policy Id</p>
              <p className="text-sm font-medium text-foreground">{model.policy_id}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Coverage Amount</p>
              <p className="text-sm font-medium text-foreground">
                {fCurrency(model.coverage_amount)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Policy Coverage</p>
              <p className="text-sm font-medium text-foreground">{model.policy_coverage}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Date of Receipt</p>
              <p className="text-sm font-medium text-foreground">
                {fDate(model.date_of_receipt, FDateFormat.localized_us_long_month_day_year)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
